/**
 * ChatFocusContext - Global state for chat full-screen focus mode
 * 
 * When chat is in focus mode:
 * - Bottom nav hides
 * - Dashboard sections hide
 * - Full immersive chat experience
 */

import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface ChatFocusContextType {
  /** Whether the chat is in full-screen focus mode */
  isChatFocused: boolean;
  /** Enter chat focus mode */
  enterChatFocus: () => void;
  /** Exit chat focus mode */
  exitChatFocus: () => void;
  /** Toggle chat focus mode */
  toggleChatFocus: () => void;
}

const ChatFocusContext = createContext<ChatFocusContextType | undefined>(undefined);

export function ChatFocusProvider({ children }: { children: ReactNode }) {
  const [isChatFocused, setIsChatFocused] = useState(false);

  const enterChatFocus = useCallback(() => {
    setIsChatFocused(true);
  }, []);

  const exitChatFocus = useCallback(() => {
    setIsChatFocused(false);
  }, []);

  const toggleChatFocus = useCallback(() => {
    setIsChatFocused(prev => !prev);
  }, []);

  return (
    <ChatFocusContext.Provider value={{ isChatFocused, enterChatFocus, exitChatFocus, toggleChatFocus }}>
      {children}
    </ChatFocusContext.Provider>
  );
}

export function useChatFocus() {
  const context = useContext(ChatFocusContext);
  if (!context) {
    // Return default values if used outside provider (graceful degradation)
    return {
      isChatFocused: false,
      enterChatFocus: () => {},
      exitChatFocus: () => {},
      toggleChatFocus: () => {},
    };
  }
  return context;
}
